import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { OrdbokEntry } from '@/types'

import Link from 'next/link'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Cta } from '@/components/generals/cta'
import { SplitWords } from '@/components/generals/split-words'
import { SectionLabel } from '@/components/generals/section-label'
import { ORDBOK } from '@/constants/ordbok'
import { LINKS } from '@/constants/site'

type Neighbour = Pick<OrdbokEntry, 'slug' | 'term'>

interface TermPageProps {
    entry: OrdbokEntry
    previous: Neighbour | null
    next: Neighbour | null
    related: Neighbour[]
    description: string
}

const SORTED: OrdbokEntry[] = [...ORDBOK].sort((a, b) => a.term.localeCompare(b.term, 'nb'))

const plain = (html: string): string => html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()

const TermPage: FC<TermPageProps> = ({ entry, previous, next, related, description }): ReactNode => (
    <Layout header='light'>
        <Seo title={entry.term} description={description} type='article' />

        <article className='tone-butter relative overflow-hidden' data-header='light'>
            <div className='grid-lines' />

            <div className='wrap relative pt-[calc(var(--header-h)+4rem)] pb-16 md:pb-24'>
                <Link href='/haandverkerordboken' className='load-fade t-label inline-flex items-center gap-2 text-tone-muted hover:text-tone-fg' style={{ '--load-delay': '0.05s' } as CSSProperties}>
                    <ArrowLeft className='size-4' aria-hidden='true' />
                    Håndverkerordboken
                </Link>

                <p className='t-label load-fade mt-12 text-tone-ink' style={{ '--load-delay': '0.1s' } as CSSProperties}>Oppslag</p>
                <h1 className='ordbok-headword mt-4'>
                    <span className='load-words block' style={{ '--load-delay': '0.15s' } as CSSProperties}>
                        <SplitWords text={entry.term} />
                    </span>
                </h1>

                <div className='load-fade mt-12 grid grid-cols-12 gap-x-6 gap-y-10 border-t border-tone-fg pt-8' style={{ '--load-delay': '0.6s' } as CSSProperties}>
                    <p className='t-label col-span-12 text-tone-muted md:col-span-3'>Forklaring</p>
                    <div className='rich-text ordbok-definition col-span-12 md:col-span-8 lg:col-span-7' dangerouslySetInnerHTML={{ __html: entry.html }} />
                </div>
            </div>
        </article>

        <nav aria-label='Flere oppslag' className='tone-paper relative py-16 md:py-24' data-header='light'>
            <div className='wrap grid grid-cols-12 gap-x-6 gap-y-14'>
                <div className='col-span-12 grid grid-cols-2 border-y border-tone-fg lg:col-span-7'>
                    {previous
                        ? (
                            <Link href={`/haandverkerordboken/${previous.slug}`} className='group flex flex-col gap-3 border-r border-tone-line py-8 pr-6'>
                                <span className='t-label inline-flex items-center gap-2 text-tone-muted'>
                                    <ArrowLeft className='size-4 transition-transform duration-500 ease-out-quint group-hover:-translate-x-1' aria-hidden='true' />
                                    Forrige
                                </span>
                                <span className='t-h3 balance'>{previous.term}</span>
                            </Link>
                        )
                        : <span className='border-r border-tone-line' />}

                    {next
                        ? (
                            <Link href={`/haandverkerordboken/${next.slug}`} className='group flex flex-col items-end gap-3 py-8 pl-6 text-right'>
                                <span className='t-label inline-flex items-center gap-2 text-tone-muted'>
                                    Neste
                                    <ArrowRight className='size-4 transition-transform duration-500 ease-out-quint group-hover:translate-x-1' aria-hidden='true' />
                                </span>
                                <span className='t-h3 balance'>{next.term}</span>
                            </Link>
                        )
                        : <span />}
                </div>

                {related.length > 0 && (
                    <div className='col-span-12 lg:col-span-4 lg:col-start-9'>
                        <SectionLabel>Flere ord på {entry.term.charAt(0).toLocaleUpperCase('nb')}</SectionLabel>
                        <ul className='mt-6 border-t border-tone-line'>
                            {related.map(item => (
                                <li key={item.slug} className='border-b border-tone-line'>
                                    <Link href={`/haandverkerordboken/${item.slug}`} className='ordbok-term group flex items-baseline justify-between gap-3 py-3'>
                                        <span>{item.term}</span>
                                        <span className='ordbok-term-arrow t-label text-tone-ink' aria-hidden='true'>Les</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </nav>

        <section id='prov-svenn' className='tone-night grain relative overflow-hidden py-20 md:py-28' data-header='dark'>
            <div className='wrap grid grid-cols-12 items-end gap-x-6 gap-y-8'>
                <p className='t-h1 col-span-12 balance lg:col-span-7'>Mindre å slå opp. Mer tid til håndverket.</p>
                <div className='col-span-12 lg:col-span-4 lg:col-start-9'>
                    <p className='text-tone-muted'>Svenn samler KS, HMS, timer og tilbud i én app. Prøv gratis i 14 dager, uten kredittkort.</p>
                    <Cta href={LINKS.signup} className='mt-6'>Prøv Svenn gratis</Cta>
                </div>
            </div>
        </section>
    </Layout>
)

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: ORDBOK.map(entry => ({ params: { slug: entry.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<TermPageProps> = async ({ params }) => {
    const slug = String(params?.slug ?? '')
    const index = SORTED.findIndex(entry => entry.slug === slug)

    if (index === -1) return { notFound: true }

    const entry = SORTED[index]
    const pick = (item: OrdbokEntry | undefined): Neighbour | null => (item ? { slug: item.slug, term: item.term } : null)
    const letter = entry.term.charAt(0).toLocaleUpperCase('nb')

    const related = SORTED
        .filter(item => item.slug !== entry.slug && item.term.charAt(0).toLocaleUpperCase('nb') === letter)
        .slice(0, 6)
        .map(item => ({ slug: item.slug, term: item.term }))

    const text = plain(entry.html)
    const description = text.length > 155 ? `${text.slice(0, 152).trimEnd()}...` : text

    return {
        props: {
            entry,
            previous: pick(SORTED[index - 1]),
            next: pick(SORTED[index + 1]),
            related,
            description
        }
    }
}

export default TermPage
